/*
 * SUPABASE MIGRATION — run in the Supabase SQL editor before using this component
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * CREATE TABLE IF NOT EXISTS sprs_score_history (
 *   id          uuid        DEFAULT gen_random_uuid() PRIMARY KEY,
 *   user_id     uuid        NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
 *   company_id  uuid,
 *   score       integer     NOT NULL,
 *   recorded_at timestamptz DEFAULT now(),
 *   notes       text
 * );
 *
 * ALTER TABLE sprs_score_history ENABLE ROW LEVEL SECURITY;
 *
 * CREATE POLICY "Users manage own SPRS history"
 *   ON sprs_score_history
 *   FOR ALL
 *   USING (auth.uid() = user_id)
 *   WITH CHECK (auth.uid() = user_id);
 *
 * CREATE INDEX idx_sprs_score_history_user_recorded
 *   ON sprs_score_history (user_id, recorded_at DESC);
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { supabase } from '../supabase';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDateFull(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function scoreColor(score) {
  if (score >= 110) return 'var(--green)';
  if (score >= 88)  return '#3b82f6';
  if (score >= 0)   return 'var(--amber)';
  return 'var(--red)';
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const score = d.score;
  const color = scoreColor(score);
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border2)',
      borderRadius: 8,
      padding: '10px 14px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
      fontFamily: 'var(--font)',
      minWidth: 140,
    }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        {fmtDateFull(d.recorded_at)}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'var(--mono)', lineHeight: 1 }}>
          {score > 0 ? `+${score}` : score}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text3)' }}>/ 110</span>
      </div>
      {d.notes && (
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6, lineHeight: 1.45, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
          {d.notes}
        </div>
      )}
    </div>
  );
}

// ── Custom dot ────────────────────────────────────────────────────────────────

function CustomDot(props) {
  const { cx, cy, payload } = props;
  const color = scoreColor(payload.score);
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill={color} stroke="var(--bg2)" strokeWidth={2} />
    </g>
  );
}

function CustomActiveDot(props) {
  const { cx, cy, payload } = props;
  const color = scoreColor(payload.score);
  return (
    <g>
      <circle cx={cx} cy={cy} r={7} fill={color} fillOpacity={0.25} stroke={color} strokeWidth={2} />
      <circle cx={cx} cy={cy} r={4} fill={color} />
    </g>
  );
}

// ── Record Score Modal ────────────────────────────────────────────────────────

function RecordModal({ onClose, onSave, currentScore }) {
  const [score, setScore]   = useState(currentScore !== undefined ? String(currentScore) : '');
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const handleSave = async () => {
    const parsed = parseInt(score, 10);
    if (isNaN(parsed) || parsed < -203 || parsed > 110) {
      setErr('Score must be between -203 and 110.');
      return;
    }
    setSaving(true);
    setErr('');
    await onSave(parsed, notes.trim());
    setSaving(false);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="glass-panel"
        style={{
          width: 360,
          background: 'var(--surface)',
          border: '1px solid var(--border2)',
          borderRadius: 12,
          padding: '24px 24px 20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          fontFamily: 'var(--font)',
        }}
      >
        {/* Modal header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 7,
              background: 'var(--blue-bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--accent)',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Record SPRS Score</span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Score input */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 10, color: 'var(--text3)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            SPRS Score <span style={{ color: 'var(--text3)' }}>(-203 to 110)</span>
          </label>
          <input
            type="number"
            min="-203"
            max="110"
            value={score}
            onChange={e => { setScore(e.target.value); setErr(''); }}
            placeholder="e.g. 88"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--bg2)', border: `1px solid ${err ? 'var(--red)' : 'var(--border2)'}`,
              borderRadius: 7, padding: '9px 12px',
              fontSize: 13, color: 'var(--text)',
              fontFamily: 'var(--mono)', outline: 'none',
            }}
          />
          {err && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>{err}</div>}
        </div>

        {/* Notes input */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 10, color: 'var(--text3)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Notes <span style={{ color: 'var(--text3)' }}>(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. After Q2 audit remediation"
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--bg2)', border: '1px solid var(--border2)',
              borderRadius: 7, padding: '9px 12px',
              fontSize: 12, color: 'var(--text)', lineHeight: 1.5,
              fontFamily: 'var(--font)', outline: 'none', resize: 'vertical',
            }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px', borderRadius: 7, fontSize: 12,
              fontFamily: 'var(--font)', fontWeight: 500,
              background: 'transparent', border: '1px solid var(--border2)',
              color: 'var(--text2)', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || score === ''}
            style={{
              padding: '8px 18px', borderRadius: 7, fontSize: 12,
              fontFamily: 'var(--font)', fontWeight: 600,
              background: saving || score === '' ? 'var(--border2)' : 'var(--accent)',
              color: saving || score === '' ? 'var(--text3)' : '#fff',
              border: 'none', cursor: saving || score === '' ? 'default' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {saving ? 'Saving…' : 'Save Score'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SPRSScoreHistory({ session, currentScore }) {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);

  const userId = session?.user?.id;

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from('sprs_score_history')
      .select('id, score, recorded_at, notes')
      .eq('user_id', userId)
      .order('recorded_at', { ascending: true })
      .limit(60);
    if (!error && data) setRows(data);
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = useCallback(async (score, notes) => {
    if (!userId) return;
    const { error } = await supabase
      .from('sprs_score_history')
      .insert({ user_id: userId, score, notes: notes || null });
    if (!error) await load();
  }, [userId, load]);

  // Chart data — add formatted label for X axis
  const chartData = rows.map(r => ({ ...r, label: fmtDate(r.recorded_at) }));

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border2)',
      borderRadius: 12,
      overflow: 'hidden',
      marginBottom: 20,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'var(--blue-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent)', flexShrink: 0,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>SPRS Score History</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
              Official DoD range: -203 to +110
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 7,
            fontSize: 11, fontWeight: 500, fontFamily: 'var(--font)',
            background: 'var(--blue-bg)',
            border: '1px solid var(--accent)',
            color: 'var(--accent)', cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--accent)' || (e.currentTarget.style.color = '#fff')}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--blue-bg)'; e.currentTarget.style.color = 'var(--accent)'; }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Record Score
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px 20px' }}>
        {loading ? (
          <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading…</div>
          </div>
        ) : chartData.length < 2 ? (
          /* Empty state */
          <div style={{
            height: 180,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 10,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 99,
              background: 'var(--surface)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text3)',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>
                No score history yet
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, maxWidth: 280 }}>
                Scores are recorded each time you run an assessment, or manually using the Record Score button.
              </div>
            </div>
            <button
              onClick={() => setShowModal(true)}
              style={{
                marginTop: 4,
                padding: '7px 16px', borderRadius: 7,
                fontSize: 11, fontWeight: 500, fontFamily: 'var(--font)',
                background: 'var(--accent)', color: '#fff',
                border: 'none', cursor: 'pointer',
              }}
            >
              Record your first score
            </button>
          </div>
        ) : (
          /* Chart */
          <div>
            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
              {[
                { color: 'var(--green)', label: '110  Perfect score' },
                { color: 'var(--amber)', label: '0  DoD threshold warning' },
              ].map(({ color, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="16" height="2" viewBox="0 0 16 2">
                    <line x1="0" y1="1" x2="16" y2="1" stroke={color} strokeWidth="1.5" strokeDasharray="3 2"/>
                  </svg>
                  <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{label}</span>
                </div>
              ))}
            </div>

            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 8, bottom: 0, left: -8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.05)"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: 'var(--text3)', fontFamily: 'var(--mono)' }}
                  axisLine={false}
                  tickLine={false}
                  dy={6}
                />
                <YAxis
                  domain={[-203, 110]}
                  ticks={[-203, -100, 0, 55, 88, 110]}
                  tick={{ fontSize: 10, fill: 'var(--text3)', fontFamily: 'var(--mono)' }}
                  axisLine={false}
                  tickLine={false}
                  width={38}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'var(--border2)', strokeWidth: 1 }} />

                {/* Reference lines */}
                <ReferenceLine
                  y={110}
                  stroke="var(--green)"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                />
                <ReferenceLine
                  y={0}
                  stroke="var(--amber)"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                />

                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="var(--accent)"
                  strokeWidth={2}
                  dot={<CustomDot />}
                  activeDot={<CustomActiveDot />}
                  isAnimationActive={true}
                  animationDuration={600}
                  animationEasing="ease-out"
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Latest score callout */}
            {chartData.length > 0 && (() => {
              const latest = chartData[chartData.length - 1];
              const color  = scoreColor(latest.score);
              const delta  = chartData.length >= 2
                ? latest.score - chartData[chartData.length - 2].score
                : null;
              return (
                <div style={{
                  marginTop: 14,
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>
                      Latest score
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'var(--mono)', lineHeight: 1 }}>
                        {latest.score > 0 ? `+${latest.score}` : latest.score}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--text3)' }}>/ 110</span>
                    </div>
                  </div>
                  {delta !== null && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 5,
                      background: delta >= 0 ? 'var(--green-bg)' : 'var(--red-bg)',
                      border: `1px solid ${delta >= 0 ? 'var(--green)' : 'var(--red)'}40`,
                    }}>
                      <svg
                        width="10" height="10" viewBox="0 0 24 24" fill="none"
                        stroke={delta >= 0 ? 'var(--green)' : 'var(--red)'}
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: delta >= 0 ? 'rotate(0deg)' : 'rotate(180deg)' }}
                      >
                        <polyline points="18 15 12 9 6 15"/>
                      </svg>
                      <span style={{
                        fontSize: 11, fontWeight: 600, fontFamily: 'var(--mono)',
                        color: delta >= 0 ? 'var(--green)' : 'var(--red)',
                      }}>
                        {delta >= 0 ? `+${delta}` : delta}
                      </span>
                    </div>
                  )}
                  <div style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text3)' }}>
                    {fmtDateFull(latest.recorded_at)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Record Score Modal */}
      {showModal && (
        <RecordModal
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          currentScore={currentScore}
        />
      )}
    </div>
  );
}
